import { handlePaymentAction, buildCloverConfirmBody, CLOVER_ACTION, CLOVER_PAYMENT_ACTION_TYPE } from '@/services/utils/clover-utils';

/** Method fingerprinting, then challenge — EMV 3DS runs at most two actions; cap as a safety net. */
export const MAX_PAYMENT_ACTIONS = 5;

/**
 * Clover 3DS runs as a post-payment action loop: a payment response may report actionRequired with a
 * Clover3ds paymentAction (method fingerprinting, then challenge). For each, run the SDK action and
 * resubmit a Confirm carrying the resulting status until no action remains.
 *
 * Two flows share this implementation (pay-at-table via services/pay.js, order-ahead via store/orders.js):
 *   - data + yourPayment: the initial payment response and this guest's payment located within it. The
 *     flows locate it differently, so the caller passes the value; the loop re-finds it by id thereafter.
 *   - postConfirm(confirmBody): resubmits the Confirm and resolves to the next payment response.
 *   - onValidatingPayment(): fires when the guest should see payment validation feedback — during the
 *     hidden method flow, and after any action while its Confirm is in flight. Not before a challenge,
 *     since the challenge iframe covers the loader.
 */
const resolveCloverPaymentActions = async ({ data, yourPayment, postConfirm, onValidatingPayment = () => {} }) => {
  let currentData = data;
  let payment = yourPayment;
  let iterations = 0;

  /* eslint-disable no-await-in-loop */
  // Any non-Clover payment will not have an actionRequired and so will just no Op return the data.
  while (payment?.actionRequired && payment.paymentAction?.type === CLOVER_PAYMENT_ACTION_TYPE) {
    if (iterations >= MAX_PAYMENT_ACTIONS) {
      throw new Error('Exceeded maximum Clover 3DS payment actions');
    }
    iterations += 1;

    const orderPaymentId = payment.id;

    if (payment.paymentAction.action === CLOVER_ACTION.METHOD_FLOW) onValidatingPayment();

    const status = await handlePaymentAction(payment.paymentAction);

    onValidatingPayment();

    const confirmBody = buildCloverConfirmBody({ orderPaymentId, status });

    currentData = await postConfirm(confirmBody);
    payment = (currentData?.payments || []).find(p => p.id === orderPaymentId);
  }
  /* eslint-enable no-await-in-loop */

  return currentData;
};

export default resolveCloverPaymentActions;
